<template>
  <m-dropdown
    :value="open"
    :disabled="readOnly"
    :title="$t('listFilter.title')"
    placement="bottomLeft"
    @update:value="emit('update:open', $event)"
  >
    <slot name="trigger" />
    <template #overlay>
      <m-card
        class="quick-goal-scope"
        no-padding
      >
        <m-content :padding-y="4">
          <m-content :padding-x="5">
            <quick-filter-header
              :label="scopeItem.text"
              @delete="emit('delete')"
              @add-to-advanced="emit('addToAdvanced')"
            >
              <template #op>
                <m-select
                  :value="selectedType"
                  :items="operatorTypes"
                  :read-only="readOnly"
                  xs
                  hide-border
                  light
                  :trigger-style="{ textTransform: 'lowercase' }"
                  @input="updateSelectedType"
                />
              </template>
            </quick-filter-header>
            <div
              v-show="!isEmpty"
              class="_input"
            >
              <goal-picker
                :value="selectedGoals"
                class="_goal-select"
                :placeholder="$t('general.empty')"
                :default-view="defaultView"
                :view-application="goalParentPicker"
                :initial-selected-cycles="selectedCycles"
                :base-filter="baseFilter"
                show-create-view
                show-views-selector
                :read-only="readOnly"
                full-width
                placement="bottomCenter"
                :max-tag-text-length="20"
                @update:value="updateSelectedGoals"
              />
            </div>
          </m-content>
        </m-content>
      </m-card>
    </template>
  </m-dropdown>
</template>

<script setup>
import QuickFilterHeader from '@/components/filter/quick/QuickFilterHeader.vue';
import useGoalDefaultProps from '@/composables/saved-views/goal-default-props';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import usePersistedGoalCycle from '@/composables/goal-cycle/persisted-goal-cycle';
import usePublishedAtFilter from '@/composables/goal/published-at-filter';
import useScopeItem from '@/composables/filter/scope-item';
import { AND } from 'shared/api/query/constants';
import { EMPTY, NOT, NOT_EMPTY } from '@/lib/filter/scope-tree';
import { GOAL_RELATION } from '@/lib/props/custom-types';
import { computed, defineAsyncComponent, ref } from 'vue';
import { viewApplication } from 'shared/constants.json';

const GoalPicker = defineAsyncComponent(() => import('@/components/goal/GoalPicker.vue'));

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  scopeItem: {
    type: Object,
    required: true,
  },
  node: {
    type: Object,
    required: true,
  },
  fieldName: {
    type: String,
    required: true,
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
  hideIsEmpty: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(['update:open', 'update:scope', 'update:not-operator', 'delete', 'addToAdvanced']);

const goalParentPicker = viewApplication.goalParentPicker;
const { loggedInUserAccount } = useLoggedInUserAccount();
const defaultPropsSvc = useGoalDefaultProps();
const defaultView = ref({
  params: {
    filter: { account: { uid: loggedInUserAccount.value.uid }, children: [], op: AND },
    order: [],
    props: defaultPropsSvc.defaultProps.value,
    applyFilterOnFirstLevelOnly: false,
  },
});
const { publishedAtFilter: baseFilter } = usePublishedAtFilter();
const { composedSelectedCycles: persistedSelectedCycles } = usePersistedGoalCycle();
const selectedCycles = ref(persistedSelectedCycles);

const { operatorTypes: getOperatorTypes } = useScopeItem();
const operatorTypes = computed(() => getOperatorTypes(GOAL_RELATION, { hideEmpty: props.hideIsEmpty }));

const isNot = computed(() => props.node.op === NOT);
const scope = computed(() => props.node.children[0].scope);
const isEmpty = computed(() => scope.value.isEmpty);

const selectedType = computed(() => {
  if (isNot.value) {
    return isEmpty.value ? NOT_EMPTY : NOT;
  }
  return isEmpty.value ? EMPTY : AND;
});
const updateSelectedType = (value) => {
  if (![NOT, NOT_EMPTY].includes(value)) {
    emit('update:not-operator', AND);
  } else {
    emit('update:not-operator', NOT);
  }
  if ([EMPTY, NOT_EMPTY].includes(value)) {
    emit('update:scope', { ...scope.value, isEmpty: true });
    return;
  }
  emit('update:scope', { ...scope.value, isEmpty: false });
};

const selectedGoal = computed(() => scope.value[props.fieldName]);
const selectedGoals = computed(() => (selectedGoal.value === null ? [] : [selectedGoal.value.uid]));
const updateSelectedGoals = (value) => {
  if (JSON.stringify(selectedGoals.value) === JSON.stringify(value)) {
    return;
  }
  emit('update:scope', { ...scope.value, [props.fieldName]: value.length > 0 ? { uid: value[0] } : null });
};
</script>

<style scoped lang="scss">
.quick-goal-scope {
  ._input {
    margin: 0 .4rem .4rem .4rem;
  }
}
</style>
